/**
 * sl-8n5m.9.4: TagDisplay ViewComponent
 *
 * Chip list of a row's tag field. Optional clickable mode generates
 * ?tag=<value> query-param links to drive a ShellLayout filter bar.
 *
 * Intentionally distinct from filter-bar (navigation role): tag-display is
 * a per-row readout, filter-bar is a global input.
 */
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type { ViewComponent } from '../../../types/view-component.types';
import { PlaceholderWarning } from '../../custom-app/placeholder-warning';
import * as styles from './tag-display.css';

const configSchema = z.object({
  tagsField: z.string().default('tags'),
  clickable: z.boolean().default(true),
  linkBase: z.string().default('?tag='),
});

function extractTags(cell: unknown): string[] {
  if (cell == null) return [];
  if (Array.isArray(cell)) {
    return cell
      .map(v =>
        typeof v === 'string' ? v : String((v as { name?: string })?.name ?? '')
      )
      .map(v => v.trim())
      .filter(Boolean);
  }
  if (typeof cell === 'string') {
    return cell
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);
  }
  if (
    typeof cell === 'object' &&
    cell !== null &&
    'name' in (cell as Record<string, unknown>)
  ) {
    const name = String((cell as { name: unknown }).name ?? '').trim();
    return name ? [name] : [];
  }
  return [];
}

export const TagDisplayComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.TAG_DISPLAY,
  type: 'content',
  name: 'Tags',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-custom-app-font-size-sm)' } },
    '🏷'
  ),
  capabilities: [
    {
      role: 'tags',
      description: 'Tag chip list rendered from a select / multi-select column',
      required: true,
      requirements: [
        { field: 'tags', acceptedTypes: ['text'], required: true },
      ],
    },
  ],
  configSchema,
  defaultConfig: {
    tagsField: 'tags',
    clickable: true,
    linkBase: '?tag=',
  },
  render({ config, data }) {
    const cfg = config as z.infer<typeof configSchema>;
    const row = data[0];
    if (!row) {
      return createElement(PlaceholderWarning, {
        reasonKey: 'tagsNoRow',
        role: 'tags',
      });
    }
    const tags = extractTags(row[cfg.tagsField]);
    if (tags.length === 0) {
      return createElement(PlaceholderWarning, {
        reasonKey: 'tagsEmpty',
        role: 'tags',
      });
    }
    return createElement(
      'div',
      {
        className: styles.list,
      },
      ...tags.map(tag =>
        createElement(
          cfg.clickable ? 'a' : 'span',
          {
            key: tag,
            className: styles.chip,
            'data-sl-field-tag': tag,
            ...(cfg.clickable
              ? { href: `${cfg.linkBase}${encodeURIComponent(tag)}` }
              : {}),
          },
          tag
        )
      )
    );
  },
  palettePreview() {
    return createElement(
      'div',
      {
        style: {
          padding: 'var(--sl-custom-app-padding-sm)',
          fontSize: 'var(--sl-custom-app-font-size-xs)',
          color: 'var(--streamlain-text-secondary-color)',
        },
      },
      '🏷 Tags'
    );
  },
};
